/**
 * A certificate of completion on the Docs card (ADR 0308): the row, and the
 * button beside Upload that makes one.
 *
 * Kept out of `DocsPanel` so the card's own list stays readable. The row
 * opens the frozen snapshot, never the customer's link, for the reasons a
 * change order's Open gives; offers the link and Withdraw only while the
 * certificate is still open; and says out loud when the signed PDF did not
 * go, because that PDF is what the insurance company gets.
 */

import { capitalise, type CertificateEntry, type Job } from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { voidCompletionCertificate } from "../../lib/queries";
import { CertificateModal } from "./CertificateModal";
import { CertificateRecordModal } from "./CertificateRecordModal";

const CERTIFICATE_STATE: Record<string, string> = {
  sent: "Sent, not opened",
  viewed: "Opened",
  signed: "Signed",
  void: "Withdrawn",
};

/**
 * The button on the Docs card. Jonathon, 2026-10-24: "this should live in
 * docs in the customer's job where we can generate it and send it to the
 * customer."
 */
export function CertificateButton({
  job,
  onCreated,
}: {
  job: Job;
  onCreated: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="btn-small"
        title={t(
          "Fill in a certificate of completion from this job and send it to the customer to sign",
        )}
        onClick={() => setOpen(true)}
      >
        {t("Certificate of completion")}
      </button>
      {open ? (
        <CertificateModal
          job={job}
          onClose={() => setOpen(false)}
          onCreated={onCreated}
        />
      ) : null}
    </>
  );
}

export function CertificateRow({
  row,
  onChanged,
  onError,
}: {
  row: CertificateEntry;
  /** The list reloads after a withdrawal. */
  onChanged: () => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const live = row.status === "sent" || row.status === "viewed";

  async function withdraw() {
    if (
      !window.confirm(
        t("Withdraw this certificate of completion?") +
          "\n\n" +
          t(
            "The link stops working for the customer. Create a new one to send another.",
          ),
      )
    )
      return;
    setBusy(true);
    try {
      await voidCompletionCertificate(row.id);
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      {reading ? (
        <CertificateRecordModal
          certificateId={row.id}
          onClose={() => setReading(false)}
        />
      ) : null}
      <td className="dname">
        {t("Certificate of completion")}
        {row.homeowner ? (
          <span className="na-hint"> · {row.homeowner}</span>
        ) : null}
      </td>
      <td className="dacts">
        <button className="btn-small dopen" onClick={() => setReading(true)}>
          {t("Open")}
        </button>
        {live ? (
          <>
            <button
              className="btn-small"
              title={t("Copy the link the customer signs on")}
              onClick={() =>
                void navigator.clipboard?.writeText(
                  `${window.location.origin}/cert/${row.token}`,
                )
              }
            >
              {t("Copy link")}
            </button>
            <button
              className="btn-small danger"
              disabled={busy}
              title={t("Kill this link")}
              onClick={() => void withdraw()}
            >
              {busy ? t("Withdrawing…") : t("Withdraw")}
            </button>
          </>
        ) : null}
      </td>
      <td>
        <span
          className={`docstate ${row.status === "signed" ? "accepted" : row.status}`}
        >
          {t(CERTIFICATE_STATE[row.status] ?? capitalise(row.status))}
        </span>
      </td>
      <td className="sub" data-label={t("Sent")}>
        {row.sent_at ? fmtDateTime(row.sent_at) : "—"}
      </td>
      <td className="sub" data-label={t("Signed")}>
        {row.signed_at ? fmtDateTime(row.signed_at) : "—"}
      </td>
      <td className="sub" data-label={t("Signed by")}>
        {row.signer_name ?? "—"}
        {row.status === "signed" && !row.copy_sent_at ? (
          <span className="na-hint">
            {" "}
            · {t("no copy sent")}
            {row.copy_error ? ` (${row.copy_error})` : ""}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
